"use client";

import React from "react";
import { useForgotPassword } from "@/hooks/auth/useForgotPassword";
import { ResetInstructions } from "./reset-instructions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTooltipFade } from "@/components/hooks/useTooltipFade.hook";

interface ForgotPasswordFormProps {
  initialEmail?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  initialEmail = "",
  onSuccess,
  onError,
}) => {
  const {
    form,
    submitForgotPassword,
    loading,
    error,
    success,
    showSuccess,
    handleBackToLogin,
    handleTryAgain,
  } = useForgotPassword({
    initialEmail,
    onSuccess,
    onError,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const emailValue = watch ? watch("email") || "" : "";

  const {
    showTooltip: showEmailTooltip,
    handleMouseEnter: handleEmailMouseEnter,
    handleMouseLeave: handleEmailMouseLeave,
    handleTooltipOpenChange: handleEmailTooltipOpenChange,
    hideTooltip: hideEmailTooltip,
  } = useTooltipFade({
    delay: 2000,
    shouldShow: (value: string) => !value || value.trim() === "",
  });

  return (
    <div className="">
      <Card className="w-full md:w-[440px] mx-auto border-border shadow-none">
        <CardContent className="md:p-8 p-6">
          <div className="text-center mb-6">
            <h1 className="md:text-[26px] text-[20px] font-semibold text-foreground md:mb-4 mb-2 text-balance leading-tight">
              Forgot Your Password?
            </h1>
            <p className="md:text-base !text-sm text-muted-foreground text-center leading-relaxed">
              Enter your email address below, then check your email for more
              instructions on how to reset your password.
            </p>
          </div>
          {success && (
            <div className="mb-3 bg-[#51cf661a] p-4 rounded-md">
              <p className=" text-sm ">
                We have emailed your password reset link.
              </p>
            </div>
          )}
          <form
            onSubmit={handleSubmit(submitForgotPassword)}
            className="space-y-6"
          >
            <div className="space-y-2">
              <Label htmlFor="email" className="sr-only">
                Email
              </Label>
              <TooltipProvider delayDuration={0}>
                <Tooltip
                  open={showEmailTooltip}
                  onOpenChange={handleEmailTooltipOpenChange}
                >
                  <TooltipTrigger asChild>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Email"
                      className="border-input"
                      disabled={loading || success}
                      {...register("email", {
                        onChange: (e) => {
                          // Hide tooltip immediately when user starts typing
                          hideEmailTooltip();
                        },
                      })}
                      onMouseEnter={() => handleEmailMouseEnter(emailValue)}
                      onMouseLeave={handleEmailMouseLeave}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="end"
                    className="bg-gray-800 text-white animate-in fade-in-0 duration-300 slow-fade-out p-2 h-6"
                    data-testid="forgot-password-email-tooltip"
                  >
                    <p className="text-xs leading-2">
                      Please fill out this field
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {errors.email && (
                <p className="text-sm text-destructive">
                  {String(errors.email.message)}
                </p>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <Button
              type="submit"
              className="w-full h-[42px] cursor-pointer"
              disabled={loading || success}
            >
              {loading ? "Sending..." : "Send Reset Instructions"}
            </Button>
          </form>

          <div className="text-center mt-6">
            <span className="text-[14px] font-normal text-muted-foreground">
              Accidentally here? No problem, just{" "}
              <button
                type="button"
                onClick={handleBackToLogin}
                className="text-[oklch(0.67_0.15_238.95)] hover:underline cursor-pointer"
              >
                Login
              </button>
              .
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
